import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  BookOpen,
  Calendar,
  Star,
  DollarSign,
  Clock,
  Utensils,
  Edit,
  Trash2,
  Copy,
  Play,
  Pause,
  Eye,
  MoreVertical,
  CheckCircle,
  XCircle,
  AlertCircle,
  Image as ImageIcon,
  Settings,
  Users,
  TrendingUp,
  HelpCircle
} from 'lucide-react'
import { Course } from '../types'
import coursesService from '../services/coursesService'
import mediaService from '../services/mediaService'
import toast from 'react-hot-toast'

const CourseDetails: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>()
  const navigate = useNavigate()
  const [course, setCourse] = useState<Course | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('overview')
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showDuplicateModal, setShowDuplicateModal] = useState(false)
  const [showFeaturedModal, setShowFeaturedModal] = useState(false)

  useEffect(() => {
    if (courseId) {
      fetchCourseDetails()
    }
  }, [courseId])

  const fetchCourseDetails = async () => {
    try {
      setLoading(true)
      const response = await coursesService.getCourseById(courseId!)
      if (response.success) {
        // Backend returns { success: true, data: { course: Course } }
        // Extract the course from the nested structure
        const courseData = (response.data as any).course || response.data
        setCourse(courseData)
      }
    } catch (error) {
      console.error('Error fetching course details:', error)
      toast.error('Failed to fetch course details')
      navigate('/courses')
    } finally {
      setLoading(false)
    }
  }

  const handleToggleStatus = async () => {
    if (!course) return
    
    try {
      const response = await coursesService.toggleCourseStatus(course._id)
      if (response.success) {
        setCourse({ ...course, isActive: !course.isActive })
        toast.success(`Course ${course.isActive ? 'deactivated' : 'activated'} successfully`)
      }
    } catch (error: any) {
      console.error('Error toggling course status:', error)
      const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.response?.data?.details || error?.message || 'Failed to update course status'
      toast.error(msg)
    }
  }

  const handleToggleFeatured = async () => {
    if (!course) return
    
    try {
      const response = await coursesService.toggleCourseFeatured(course._id)
      if (response.success) {
        setCourse({ ...course, isFeatured: !course.isFeatured })
        toast.success(`Course ${course.isFeatured ? 'removed from' : 'added to'} featured`)
      }
    } catch (error: any) {
      console.error('Error toggling course featured:', error)
      const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.response?.data?.details || error?.message || 'Failed to update course featured status'
      toast.error(msg)
    }
  }

  const handleDeleteCourse = async () => {
    if (!course) return
    
    try {
      const response = await coursesService.deleteCourse(course._id)
      if (response.success) {
        toast.success('Course deleted successfully')
        navigate('/courses')
      }
    } catch (error: any) {
      console.error('Error deleting course:', error)
      const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.response?.data?.details || error?.message || 'Failed to delete course'
      toast.error(msg)
    }
  }

  const handleDuplicateCourse = async () => {
    if (!course) return
    
    try {
      const response = await coursesService.duplicateCourse(course._id) as { success: boolean; data?: { _id: string } }
      if (response.success && response.data?._id) {
        toast.success('Course duplicated successfully')
        navigate(`/courses/${response.data._id}`)
      }
    } catch (error: any) {
      console.error('Error duplicating course:', error)
      const msg = error?.response?.data?.error?.message || error?.response?.data?.message || error?.response?.data?.details || error?.message || 'Failed to duplicate course'
      toast.error(msg)
    }
  }

  const getCourseTypeColor = (type?: string) => {
    if (!type) return 'bg-gray-100 text-gray-800'
    switch (type) {
      case 'TRAINING': return 'bg-blue-100 text-blue-800'
      case 'WORKSHOP': return 'bg-purple-100 text-purple-800'
      case 'RETREAT': return 'bg-green-100 text-green-800'
      case 'CLASS': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getDifficultyColor = (difficulty?: string) => {
    if (!difficulty) return 'bg-gray-100 text-gray-800'
    switch (difficulty) {
      case 'BEGINNER': return 'bg-green-100 text-green-800'
      case 'INTERMEDIATE': return 'bg-yellow-100 text-yellow-800'
      case 'ADVANCED': return 'bg-red-100 text-red-800'
      case 'ALL_LEVELS': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'media', label: 'Media', icon: ImageIcon },
    { id: 'settings', label: 'Settings', icon: Settings }
  ]

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading course details...</p>
        </div>
      </div>
    )
  }

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Course not found</h2>
          <p className="text-gray-600 mb-4">The course you're looking for doesn't exist or has been deleted.</p>
          <button
            onClick={() => navigate('/courses')}
            className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Courses
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="relative overflow-hidden bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 shadow-2xl rounded-2xl mb-6">
          <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 via-primary-700/20 to-primary-800/20"></div>
          <div className="relative flex items-center justify-between p-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/courses')}
                className="p-2 text-white/80 hover:text-white rounded-lg hover:bg-white/20 transition-all"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="relative">
                <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center shadow-xl ring-2 ring-white/30">
                  <BookOpen className="h-6 w-6 text-white" />
                </div>
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full border-2 border-white shadow-lg"></div>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">{course.name}</h1>
                <p className="text-white/90 text-sm">Course Details</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                  onClick={handleToggleStatus}
                  className={`inline-flex items-center px-4 py-2 text-sm font-semibold rounded-xl transition-all duration-300 border shadow-lg hover:shadow-xl hover:scale-105 ${
                    course.isActive
                      ? 'text-white bg-red-500/80 hover:bg-red-500 border-red-400/50'
                      : 'text-white bg-green-500/80 hover:bg-green-500 border-green-400/50'
                  }`}
                >
                  {course.isActive ? <Pause className="h-4 w-4 mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                  {course.isActive ? 'Deactivate' : 'Activate'}
                </button>
                
                <button
                  onClick={() => navigate(`/courses/${courseId}/edit`)}
                  className="flex items-center px-4 py-2 text-sm font-semibold text-white bg-white/20 backdrop-blur-sm rounded-xl hover:bg-white/30 transition-all duration-300 border border-white/30 shadow-lg hover:shadow-xl hover:scale-105"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </button>
                
                <div className="relative">
                  <button className="p-2 text-white/80 hover:text-white rounded-lg hover:bg-white/20 transition-all">
                    <MoreVertical className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Course Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden mb-8"
        >
          <div className="relative h-64 bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700">
            {(course.media?.images?.length || course.images?.length) ? (
              <img
                src={mediaService.getMediaUrlFromKey(course.media?.images?.[0] || course.images?.[0] || '')}
                alt={course.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <BookOpen className="h-24 w-24 text-white/50" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
            
            {/* Course Status Badges */}
            <div className="absolute top-4 right-4 flex space-x-2">
              {course.isFeatured && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  <Star className="h-3 w-3 mr-1" />
                  Featured
                </span>
              )}
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                course.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {course.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>
          </div>
          
          <div className="p-8">
            <div className="flex items-start justify-between mb-6">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-4">
                  {course.type && (
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getCourseTypeColor(course.type)}`}>
                      {course.type.replace('_', ' ')}
                    </span>
                  )}
                  {course.difficulty && (
                    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getDifficultyColor(course.difficulty)}`}>
                      {course.difficulty}
                    </span>
                  )}
                </div>
                
                <h2 className="text-3xl font-bold text-gray-900 mb-4">{course.name}</h2>
                
                {course.shortDescription && (
                  <p className="text-lg text-gray-600 mb-6">{course.shortDescription}</p>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-primary-100 rounded-lg">
                      <DollarSign className="h-5 w-5 text-primary-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Price</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {course.price !== undefined && course.price !== null ? (
                          <>
                            {course.currency} {course.price.toLocaleString()}
                          </>
                        ) : (
                          <span className="text-sm text-gray-500 italic">Price varies by room type</span>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <Clock className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Duration</p>
                      <p className="text-lg font-semibold text-gray-900">{course.duration} days</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Users className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Capacity</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {course.totalBookings || 0}{course.maxStudents ? ` / ${course.maxStudents}` : ''}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-yellow-100 rounded-lg">
                      <Star className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Rating</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {course.rating ? course.rating.toFixed(1) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'overview' && (
            <div className="space-y-8">
              {/* Course Overview / Key Information */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Overview</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {course.type && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Course Type</p>
                      <p className="text-gray-900 font-medium">{course.type.replace('_', ' ')}</p>
                    </div>
                  )}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Duration</p>
                    <p className="text-gray-900 font-medium">{course.duration} days</p>
                  </div>
                  {course.difficulty && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Difficulty</p>
                      <p className="text-gray-900 font-medium">{course.difficulty.replace('_', ' ')}</p>
                    </div>
                  )}
                  {course.maxStudents != null && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Max Students</p>
                      <p className="text-gray-900 font-medium">{course.maxStudents}</p>
                    </div>
                  )}
                  {course.minStudents != null && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Min Students</p>
                      <p className="text-gray-900 font-medium">{course.minStudents}</p>
                    </div>
                  )}
                  {course.yogaAllianceCertified != null && (
                    <div>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Yoga Alliance Certified</p>
                      <p className="text-gray-900 font-medium">{course.yogaAllianceCertified ? 'Yes' : 'No'}</p>
                    </div>
                  )}
                  {course.yogaStyles && course.yogaStyles.length > 0 && (
                    <div className="sm:col-span-2">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Yoga Styles</p>
                      <p className="text-gray-900 font-medium">{course.yogaStyles.join(', ')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Course Description */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Description</h3>
                {course.shortDescription && (
                  <p className="text-gray-600 leading-relaxed mb-4 font-medium">{course.shortDescription}</p>
                )}
                <p className="text-gray-600 leading-relaxed">{course.description}</p>
              </div>

              {/* Course Schedule */}
              {course.schedule && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Schedule</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Course date ranges</h4>
                      <div className="space-y-2">
                        {course.schedule.dateRanges?.length ? (
                          <ul className="space-y-2">
                            {course.schedule.dateRanges.map((range, idx) => (
                              <li
                                key={idx}
                                className="flex items-center space-x-2 text-sm text-gray-600"
                              >
                                <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                <span>
                                  {new Date(range.startDate).toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  })}
                                  {' – '}
                                  {new Date(range.endDate).toLocaleDateString('en-GB', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                  })}
                                </span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <>
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                Start: {course.schedule.startDate ? new Date(course.schedule.startDate).toLocaleDateString() : 'TBD'}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Calendar className="h-4 w-4 text-gray-400" />
                              <span className="text-sm text-gray-600">
                                End: {course.schedule.endDate ? new Date(course.schedule.endDate).toLocaleDateString() : 'TBD'}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                    
                    {course.schedule.timeSlots && course.schedule.timeSlots.length > 0 && (
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">Daily Schedule</h4>
                        <div className="space-y-2">
                          {course.schedule.timeSlots.map((slot, index) => (
                            <div key={index} className="flex items-center space-x-2 text-sm text-gray-600">
                              <Clock className="h-4 w-4 text-gray-400 flex-shrink-0" />
                              <span>
                                {slot.label ? `${slot.label}: ` : ''}{slot.day} {slot.startTime} - {slot.endTime}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Who Is This For */}
              {course.whoIsThisFor && course.whoIsThisFor.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Who Is This For?</h3>
                  <ul className="space-y-2">
                    {course.whoIsThisFor.map((item, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Users className="h-4 w-4 text-primary-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Course Highlights */}
              {course.courseHighlights && course.courseHighlights.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Highlights</h3>
                  <ul className="space-y-2">
                    {course.courseHighlights.map((highlight, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Star className="h-4 w-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* What You Will Gain */}
              {course.whatYouWillGain && course.whatYouWillGain.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">What You Will Gain</h3>
                  <ul className="space-y-2">
                    {course.whatYouWillGain.map((benefit, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <TrendingUp className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Detailed Curriculum */}
              {course.curriculum && course.curriculum.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Detailed Curriculum</h3>
                  <div className="space-y-6">
                    {course.curriculum.map((section, index) => (
                      <div key={index} className="border-l-4 border-primary-500 pl-4">
                        <h4 className="font-semibold text-gray-900 mb-3">{section.title}</h4>
                        <ul className="space-y-2">
                          {section.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="flex items-start space-x-2 text-gray-600">
                              <span className="text-primary-500 mt-1">•</span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Timetable */}
              {course.timetable && course.timetable.length > 0 && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Daily Timetable</h3>
                  <ul className="space-y-2">
                    {course.timetable.map((item, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <Clock className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Course Inclusions & Requirements */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {course.inclusions && course.inclusions.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">What's Included</h3>
                    <ul className="space-y-2">
                      {course.inclusions.map((inclusion, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-gray-600">{inclusion}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {course.requirements && course.requirements.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Requirements</h3>
                    <ul className="space-y-2">
                      {course.requirements.map((requirement, index) => (
                        <li key={index} className="flex items-center space-x-2">
                          <AlertCircle className="h-4 w-4 text-yellow-500" />
                          <span className="text-gray-600">{requirement}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              {/* Accommodation */}
              {course.accommodation && course.accommodation.available && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Accommodation</h3>
                  
                  {course.accommodation.rooms && course.accommodation.rooms.length > 0 && (
                    <div className="mb-6">
                      <h4 className="font-medium text-gray-900 mb-3">Room Types</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {course.accommodation.rooms.map((room, index) => (
                          <div key={index} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h5 className="font-medium text-gray-900">{room.type}</h5>
                              <span className="text-sm font-semibold text-primary-600">
                                {course.currency} {room.price}
                              </span>
                            </div>
                            {room.bookingAmount != null && room.bookingAmount > 0 && (
                              <p className="text-sm text-gray-600 mb-2">
                                Booking amount: {course.currency} {room.bookingAmount}
                              </p>
                            )}
                            <p className="text-sm text-gray-600 mb-2">
                              Capacity: {room.capacity} person{room.capacity > 1 ? 's' : ''}
                            </p>
                            {room.amenities && room.amenities.length > 0 && (
                              <p className="text-sm text-gray-600 mb-2">
                                Amenities: {room.amenities.join(', ')}
                              </p>
                            )}
                            {room.description && (
                              <p className="text-sm text-gray-500">{room.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {course.accommodation.mealPlan && course.accommodation.mealPlan.included && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Meal Plan</h4>
                      <div className="flex items-center space-x-2">
                        <Utensils className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">
                          {course.accommodation.mealPlan.type}
                          {course.accommodation.mealPlan.description && ` - ${course.accommodation.mealPlan.description}`}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Course Info / Metadata */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Info</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                  {course.slug && (
                    <div>
                      <p className="text-gray-500">Slug</p>
                      <p className="font-mono text-gray-900">{course.slug}</p>
                    </div>
                  )}
                  {course.createdAt && (
                    <div>
                      <p className="text-gray-500">Created</p>
                      <p className="text-gray-900">{new Date(course.createdAt).toLocaleDateString()}</p>
                    </div>
                  )}
                  {course.updatedAt && (
                    <div>
                      <p className="text-gray-500">Last Updated</p>
                      <p className="text-gray-900">{new Date(course.updatedAt).toLocaleDateString()}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* FAQ Section */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-primary-600" />
                  Frequently Asked Questions
                </h3>
                {course.faq && course.faq.length > 0 ? (
                  <div className="space-y-4">
                    {course.faq.map((item, index) => (
                      <div key={index} className="border-b border-gray-200 last:border-b-0 last:pb-0 pb-4">
                        <h4 className="font-medium text-gray-900 mb-1.5">{item.question}</h4>
                        <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No FAQs added. Edit the course to add frequently asked questions.</p>
                )}
              </div>
            </div>
          )}

          {activeTab === 'media' && (() => {
            const allImages = [...(course.media?.images || []), ...(course.images || [])]
            const allVideos = course.media?.videos || []
            const hasMedia = allImages.length > 0 || allVideos.length > 0
            const toDisplayUrl = (keyOrUrl: string) => mediaService.getMediaUrlFromKey(keyOrUrl)
            return (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Media</h3>

              {hasMedia ? (
                <div className="space-y-6">
                  {allImages.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Images</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {allImages.map((image, index) => (
                          <div key={index} className="relative group">
                            <img
                              src={toDisplayUrl(image)}
                              alt={`Course image ${index + 1}`}
                              className="w-full h-48 object-cover rounded-lg"
                            />
                            <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all duration-200 rounded-lg flex items-center justify-center">
                              <button className="opacity-0 group-hover:opacity-100 text-white p-2 bg-black bg-opacity-50 rounded-full">
                                <Eye className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {allVideos.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-3">Videos</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {allVideos.map((video, index) => (
                          <video key={index} src={toDisplayUrl(video)} controls className="w-full rounded-lg" />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No media uploaded</h3>
                  <p className="text-gray-500">Upload images and videos to showcase your course.</p>
                </div>
              )}
            </div>
            )
          })()}

          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Course Actions</h3>
                <div className="space-y-4">
                  <button
                    onClick={() => navigate(`/courses/${courseId}/edit`)}
                    className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-primary-700 bg-primary-100 rounded-lg hover:bg-primary-200 transition-colors"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Course
                  </button>
                  
                  <button
                    onClick={() => setShowDuplicateModal(true)}
                    className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicate Course
                  </button>
                  
                  <button
                    onClick={() => setShowFeaturedModal(true)}
                    className={`w-full flex items-center justify-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                      course.isFeatured
                        ? 'text-yellow-700 bg-yellow-100 hover:bg-yellow-200'
                        : 'text-gray-700 bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    <Star className="h-4 w-4 mr-2" />
                    {course.isFeatured ? 'Remove from Featured' : 'Add to Featured'}
                  </button>
                  
                  <button
                    onClick={() => setShowDeleteModal(true)}
                    className="w-full flex items-center justify-center px-4 py-3 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Course
                  </button>
                </div>
              </div>
              {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">Delete Course</h3>
              <p className="text-sm text-gray-500 text-center mb-4">
                Are you sure you want to delete "{course.name}"? This action cannot be undone.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowDeleteModal(false)
                    handleDeleteCourse()
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Course Confirmation Modal */}
      {showDuplicateModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 mb-4">
                <Copy className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">Duplicate Course</h3>
              <p className="text-sm text-gray-500 text-center mb-4">
                Are you sure you want to duplicate "{course.name}"? A copy will be created with the same content. Reviews, bookings, and ratings will be reset.
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setShowDuplicateModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowDuplicateModal(false)
                    handleDuplicateCourse()
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700"
                >
                  Duplicate
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Remove from Featured Confirmation Modal */}
      {showFeaturedModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className={`mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4 ${course.isFeatured ? 'bg-gray-100' : 'bg-yellow-100'}`}>
                <Star className={`h-6 w-6 ${course.isFeatured ? 'text-gray-600' : 'text-yellow-600'}`} />
              </div>
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                {course.isFeatured ? 'Remove from Featured' : 'Add to Featured'}
              </h3>
              <p className="text-sm text-gray-500 text-center mb-4">
                {course.isFeatured
                  ? `Are you sure you want to remove "${course.name}" from featured courses? It will no longer appear in the featured section.`
                  : `Are you sure you want to add "${course.name}" to featured courses? It will be highlighted on the homepage.`}
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => setShowFeaturedModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowFeaturedModal(false)
                    handleToggleFeatured()
                  }}
                  className={`px-4 py-2 text-sm font-medium text-white rounded-md ${
                    course.isFeatured
                      ? 'bg-gray-600 hover:bg-gray-700'
                      : 'bg-yellow-600 hover:bg-yellow-700'
                  }`}
                >
                  {course.isFeatured ? 'Remove' : 'Add to Featured'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
            </div>
          )}
        </motion.div>
      </div>

      
  )
}

export default CourseDetails
